import React, {FC, useMemo} from 'react'

import ErrorSharpIcon from '@mui/icons-material/ErrorSharp'
import InfoSharpIcon from '@mui/icons-material/InfoSharp'
import WarningSharpIcon from '@mui/icons-material/WarningSharp'
import {Box, Chip, Theme} from '@mui/material'
import fileSize from 'filesize'
import {makeStyles} from 'tss-react/mui'

import {FilePreview} from '@progyny/components/FilePreview'
import {Document} from '@progyny/store/claims'

interface FileInfoListProps {
  documents: Document[]
  removeFile: React.Dispatch<File>
  hideFileSize?: boolean
}

const useStyles = makeStyles()((theme: Theme) => ({
  chipRoot: {
    padding: `${theme.spacing(0.5)} ${theme.spacing(0.5)}`,
    height: 'auto',
    borderRadius: 3,
  },
  filesizeWarning: {
    borderColor: theme.palette.warning.main,
    color: theme.palette.warning.main,
  },
  filesizeError: {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  },
}))

export function calculateTotalSize(documents: Array<Document>): number {
  return documents.reduce<number>((prev, cur) => prev + cur.file.size, 0)
}

export const FileMax = 20 * 1024 * 1024
const TotalMax = 500 * 1024 * 1024
const EightyPercent = 0.8 * TotalMax

const FileInfoList: FC<FileInfoListProps> = (props) => {
  const {documents} = props
  const {classes} = useStyles()
  const totalFileSize = useMemo<number>(
    () => calculateTotalSize(documents),
    [documents]
  )
  const isOverEightyPercent = useMemo<boolean>(
    () => totalFileSize > EightyPercent,
    [totalFileSize]
  )
  const isOverMax = useMemo<boolean>(
    () => totalFileSize > TotalMax,
    [totalFileSize]
  )

  return (
    <>
      {documents.map((item, idx) => (
        <FilePreview
          file={item.file}
          docType={item.docType}
          description={item.description}
          key={`valid-file-${idx}`}
          removeFile={props.removeFile}
          hideFileSize={props.hideFileSize}
        />
      ))}
      <Box
        mt={2}
        mb={{xs: 2, sm: 0}}
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'flex-end'}
      >
        {documents.length > 0 && !props.hideFileSize && (
          <Chip
            label={`${fileSize(totalFileSize, {
              standard: 'jedec',
            })} / 500MB`}
            color={'primary'}
            icon={
              isOverMax ? (
                <ErrorSharpIcon />
              ) : isOverEightyPercent ? (
                <WarningSharpIcon />
              ) : (
                <InfoSharpIcon />
              )
            }
            variant={'outlined'}
            classes={{root: classes.chipRoot}}
            className={
              isOverMax
                ? classes.filesizeError
                : isOverEightyPercent
                  ? classes.filesizeWarning
                  : undefined
            }
          />
        )}
      </Box>
    </>
  )
}

export default FileInfoList
